import React from 'react';
import {
  FlexGridCheckboxCellRoot,
  FlexGridCellRoot,
  FlexGridListRoot,
  FlexGridRowListItem,
  FlexGridRowListItemButton,
  FlexGridRowRoot,
} from './styles';

/**
 * Flex-column list with CSS zebra banding by default (realm/room assignment look).
 *
 * @param {number} [inset] - theme spacing units for marginLeft; list width is calc'd so it won't overflow
 * @param {number|string} [maxWidth] - optional centered max width
 * @param {boolean} [zebra=true] - enable zebra banding
 * @param {'css'|'prop'|'none'} [zebraMode='css'] - css = nth-child on children; prop = per-row striped; none = off
 */
export function FlexGridList({
  children,
  inset = 0,
  maxWidth,
  zebra = true,
  zebraMode = 'css',
  className,
  style,
  sx,
  id,
  role,
  // withInfiniteScroll spreads parent props onto ListWrapped — ignore non-DOM junk.
  ..._ignored
}) {
  return (
    <FlexGridListRoot
      inset={inset}
      maxWidth={maxWidth}
      zebra={zebra}
      zebraMode={zebraMode}
      className={className}
      style={style}
      sx={sx}
      id={id}
      role={role}
    >
      {children}
    </FlexGridListRoot>
  );
}

/** Drop-in alias for assignment ZebraList. */
export function ZebraList(props) {
  return <FlexGridList zebra zebraMode="css" {...props} />;
}

/** Drop-in alias for assignment MarginLeftZebraList (inset spacing(3)). */
export function MarginLeftZebraList(props) {
  return <FlexGridList inset={3} zebra zebraMode="css" {...props} />;
}

/**
 * List row chrome (LocationRowWrap / RowSpace / CheckpointListRow / table rows).
 */
export const FlexGridRow = React.forwardRef(function FlexGridRow(
  {
    children,
    header = false,
    dimmed = false,
    striped = false,
    rowVariant,
    fullBleed = false,
    layout = 'block',
    dense = false,
    hover = false,
    clickable = false,
    px,
    listItem = false,
    button = false,
    alignItems,
    component,
    ...rest
  },
  ref
) {
  const rowProps = {
    ref,
    header,
    dimmed,
    striped,
    rowVariant,
    fullBleed,
    layout,
    dense,
    hover,
    clickable,
    px,
    listItem: listItem || button,
    alignItems,
    ...rest,
  };

  if (button) {
    return <FlexGridRowListItemButton {...rowProps}>{children}</FlexGridRowListItemButton>;
  }

  if (listItem) {
    return <FlexGridRowListItem {...rowProps}>{children}</FlexGridRowListItem>;
  }

  if (component) {
    return (
      <FlexGridRowRoot component={component} {...rowProps}>
        {children}
      </FlexGridRowRoot>
    );
  }

  return <FlexGridRowRoot {...rowProps}>{children}</FlexGridRowRoot>;
});

/** Optional faux-table cell for manage/courses/alerts-style column layouts. */
export function FlexGridCell({ children, width, flexGrow, hideBelow, stackAt, alignItems, minWidth, ...rest }) {
  return (
    <FlexGridCellRoot
      width={width}
      minWidth={minWidth}
      flexGrow={flexGrow}
      hideBelow={hideBelow}
      stackAt={stackAt}
      alignItems={alignItems}
      {...rest}
    >
      {children}
    </FlexGridCellRoot>
  );
}

export function FlexGridCheckboxCell({ children, ...rest }) {
  return <FlexGridCheckboxCellRoot {...rest}>{children}</FlexGridCheckboxCellRoot>;
}

export { FlexGridRowListItem };
export { FlexGridDataTable } from './FlexGridDataTable';
export { FlexGridSortableProvider, FlexGridSortableRow } from './sortable';
export {
  DEFAULT_LIST_HINT,
  EmptyPlaceholder,
  FlexGridIconLabel,
  FlexGridListHint,
  FlexGridSortHeader,
  cellWidth,
  listIconColor,
  listIconSx,
  renderFlexGridColumns,
  renderFlexGridHeaderCells,
  resolveColumns,
} from './helpers';
export default FlexGridList;
